import { useMemo, useRef, useState } from 'react';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';

import { Button, DataTable, Icon, Modal, Pagination, Radio, RadioGroup, Spinner, Text } from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { useCountries, useGetLocations, usePortNames, useStates } from '../../../hooks';
import {
  type LocationRow,
  type LocationSearchFormValues,
  type LocationsParams,
  locationSearchParamsSchema,
} from '../../../models';
import { LocationSearchForm, type SearchFormRef } from '../location-search-form';

import styles from './location-search-modal.module.css';

const EMPTY_CELL = '—';

const SORT_COLUMN_TO_API: Record<string, string> = {
  city: 'City',
  countryName: 'CountryCode',
  locationCode: 'LocationCode',
  portName: 'PortName',
  postalCodeFrom: 'PostalFrom',
  stateCode: 'StateCode',
};

const SORT_API_TO_COLUMN: Record<string, string> = {
  City: 'city',
  CountryCode: 'countryName',
  LocationCode: 'locationCode',
  PortName: 'portName',
  PostalFrom: 'postalCodeFrom',
  StateCode: 'stateCode',
};

interface LocationSearchModalProps {
  defaultValues?: LocationSearchFormValues;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange?: (value: LocationRow) => void;
}

export const LocationSearchModal: React.FC<LocationSearchModalProps> = ({
  defaultValues,
  isOpen = false,
  setIsOpen,
  onSelectionChange,
}) => {
  const [params, setParams] = useState<LocationsParams>(v.getDefaults(locationSearchParamsSchema));
  const [selectedLocation, setSelectedLocation] = useState<LocationRow | null>(null);

  const { data: locations, isFetching } = useGetLocations(params);

  const { data: countries } = useCountries();
  const { data: states } = useStates({ CountryCode: params.countryCode ?? '' });

  const portKeys = useMemo(
    () => Array.from(new Set((locations?.data ?? []).filter((row) => row.portKey).map((row) => String(row.portKey)))),
    [locations?.data],
  );
  const { data: portNames } = usePortNames(useMemo(() => ({ searchParams: { ids: portKeys } }), [portKeys]));

  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);

  const sortColumnId = params.sorting ? SORT_API_TO_COLUMN[params.sorting] : undefined;
  const tableSorting: { desc: boolean; id: string }[] =
    sortColumnId !== undefined ? [{ desc: false, id: sortColumnId }] : [];

  const reset = () => {
    setSelectedLocation(null);
    setParams(v.getDefaults(locationSearchParamsSchema));
  };

  const close = () => {
    searchFormRef.current?.reset?.();
    setSelectedLocation(null);
    setIsOpen(false);
  };

  const submit = (values: LocationSearchFormValues) => {
    setParams((prev) => ({
      ...prev,
      city: values.city,
      countryCode: String(values.countryCode?.id) ?? '',
      locationCode: values.locationCode,
      offset: 0, // Reset to first page on new search
      portKey: values.portKey,
      sorting: undefined,
      stateCode: values.stateCode?.id,
    }));
  };

  const handleSortingChange = (newSorting: { desc: boolean; id: string }[]) => {
    setParams((prev) => {
      const [sort] = newSorting;
      if (!sort || sort.desc) {
        return { ...prev, sorting: undefined };
      }
      const apiKey = SORT_COLUMN_TO_API[sort.id];
      if (!apiKey) {
        return prev;
      }

      return { ...prev, sorting: apiKey };
    });
  };

  const handleSubmitSelection = () => {
    if (selectedLocation && onSelectionChange) {
      onSelectionChange(selectedLocation);
      close();
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => setIsOpen(value)} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          Location Search
        </Text>
        <Icon name="x" onClick={close} cursor="pointer" />
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <LocationSearchForm
          defaultValues={defaultValues}
          formRef={searchFormRef}
          handleSubmit={submit}
          handleReset={reset}
        />
        {isFetching ? (
          <div className={cx(styles.tableContainer, styles.loader)}>
            <Spinner size="lg" />
          </div>
        ) : locations?.data.length ? (
          <div className={styles.results}>
            <div className={styles.total}>
              <Text weight="bold">{`${locations.total} locations `}</Text>are found
            </div>
            <div className={styles.tableContainer}>
              <DataTable<LocationRow>
                columns={[
                  {
                    accessorKey: 'locationKey',
                    cell: (props) => {
                      const row = props.row.original;
                      const isSelected = selectedLocation?.locationKey === row.locationKey;

                      return (
                        <RadioGroup
                          value={isSelected ? String(row.locationKey) : ''}
                          onChange={() => setSelectedLocation(row)}
                          className={styles.radio}
                        >
                          <Radio value={String(row.locationKey)} className={styles.radio} />
                        </RadioGroup>
                      );
                    },
                    enableSorting: false,
                    header: '',
                    maxSize: 0,
                  },
                  {
                    accessorKey: 'locationCode',
                    cell: (props) => props.row.original.locationCode || EMPTY_CELL,
                    header: 'Location ID',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'countryName',
                    cell: (props) => {
                      const { countryCode, countryName } = props.row.original;
                      return countries?.map[String(countryCode)]?.label || countryName || countryCode || EMPTY_CELL;
                    },
                    header: 'Country',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'stateCode',
                    cell: (props) => {
                      const { stateCode, stateName } = props.row.original;
                      return states?.map[String(stateCode)]?.label || stateName || stateCode || EMPTY_CELL;
                    },
                    header: 'State/Province',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'city',
                    cell: (props) => props.row.original.city || EMPTY_CELL,
                    header: 'City',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'portName',
                    cell: (props) => {
                      const { portKey, portName } = props.row.original;
                      return portNames?.map[String(portKey)]?.label || portName || EMPTY_CELL;
                    },
                    header: 'Port Name',
                    sortingFn: () => 0,
                  },
                  {
                    accessorKey: 'postalCodeFrom',
                    cell: (props) => props.row.original.postalCodeFrom || EMPTY_CELL,
                    header: 'Postal From',
                    sortingFn: () => 0,
                  },
                ]}
                data={locations.data}
                isLoading={isFetching}
                page={params.offset / params.limit + 1}
                limit={params.limit}
                totalItems={locations.total}
                sorting={tableSorting}
                onSortingChange={handleSortingChange}
              />
            </div>

            <Pagination
              className={styles.pagination}
              totalItems={locations.total}
              limit={params.limit}
              page={params.offset / params.limit + 1}
              onPageChange={(page: number) => {
                setParams((prev) => ({ ...prev, offset: (page - 1) * prev.limit }));
              }}
              onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
              onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
            />
          </div>
        ) : (
          <NoData
            className={styles.noData}
            description="No data to display. Please use the search above to find locations."
          />
        )}
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button outline secondary onClick={close}>
          Cancel
        </Button>
        <Button isDisabled={!selectedLocation} onClick={handleSubmitSelection}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
